/**
 * In-app rendering for a file-backed resource, plus a download that always works.
 *
 * Which renderer runs is decided by `render_kind`, which the backend derives
 * (`db/services/resource_files.py::render_kind_for`) rather than this component re-deriving it
 * from the content type. That keeps one mapping in one place: adding a renderer here means
 * adding its content types there, not maintaining a parallel list.
 *
 *   pdf          -> fetch the bytes -> <iframe src=blobUrl>, i.e. the browser's own PDF viewer
 *   image        -> fetch the bytes -> <img src=blobUrl>
 *   markdown     -> fetch the text, render with react-markdown (same as inline markdown steps)
 *   text         -> fetch the text, render in a <pre>
 *   spreadsheet  -> fetch the bytes, SheetJS -> an HTML table per sheet
 *   presentation -> server PDF (pptx->PDF) rendered slide-by-slide with pdf.js (PdfSlideViewer);
 *                   if there is no server PDF, fetch the bytes and let pptx-preview draw a slide
 *   word         -> fetch the bytes, mammoth -> sanitized HTML (see note below)
 *   download     -> no preview; the download button is the whole affordance
 *
 * `word` is a client-only kind, NOT one the backend emits: `.docx` currently resolves to
 * `render_kind: 'download'`, so this component upgrades that to `word` when the file name/type
 * says `.docx`. That keeps Word preview working without a backend/render_kind deploy. (mammoth
 * only reads the `.docx` XML format — legacy binary `.doc` stays a download.)
 *
 * Two things follow from that split, and both matter:
 *
 * 1. `pdf` and `image` used to hand the signed S3 URL straight to a framed/embedded tag. Under
 *    the deployment's `Cross-Origin-Embedder-Policy: require-corp` the browser refuses a
 *    cross-origin framed/embedded resource without its own COEP/CORP header, so they now read the
 *    bytes and render a same-origin `blob:` URL instead (which COEP never blocks). The pptx->PDF
 *    preview does the same before handing the blob to PdfSlideViewer.
 * 2. Every byte-reading renderer first tries the signed S3 URL DIRECTLY
 *    (one hop, no Django worker tied up relaying the file); that needs a GET CORS rule on the
 *    bucket for this origin. Where that rule is absent the cross-origin fetch rejects, so it
 *    falls back to the same-origin API relay (`fetchResourceFileContent` ->
 *    `db/resource-file/{id}/download/`), which streams the object back and always works. So the
 *    fast path is opt-in by infrastructure — add the CORS rule and these previews speed up with
 *    no code change. If BOTH paths fail (the object was removed, storage is misconfigured, or
 *    the streaming endpoint is not yet deployed) this component falls back to "preview
 *    unavailable, download instead" rather than showing an error, since the file itself is still
 *    downloadable via the signed URL.
 *
 * The heavy parsers (SheetJS, pptx-preview) are imported dynamically so they are not in the
 * initial bundle: most journey map steps are not spreadsheets, and these two are large.
 *
 * Signed URLs expire, so one is fetched per render pass and never persisted in state beyond
 * the life of the preview.
 */
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HbrIcon, HbrSpinner } from '@harbor/elements-react-standalone';
import { alertsWarningSmall, arrowSquareOut, downloadSimple } from '@harbor/elements/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import StbButton from '../../security-toolbox-ui/StbButton';
// Lazy: pdf.js (which PdfSlideViewer pulls in) is large, and most steps are not presentations —
// same reasoning as the dynamic SheetJS/pptx-preview imports below.
const PdfSlideViewer = lazy(() => import('./PdfSlideViewer'));
import {
  ResourceFileDownload,
  ResourceRenderKind,
  fetchResourceFileContent,
  formatFileSize,
  getResourceFileDownload,
  getResourceFilePreviewUrl,
} from '../../../support/resourceFiles';

type Props = {
  /** `resource.id` of a file-backed resource. */
  resourceId: number | string;
  fileName?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  renderKind?: ResourceRenderKind;
  /** Bumped by the API on every replace; changing it re-fetches the preview. */
  version?: number | null;
  /** Start with the preview open. Off by default so a step does not pull bytes unasked. */
  autoPreview?: boolean;
};

type SheetTable = { name: string; html: string };

// A client-only render kind: mammoth turns a .docx into HTML. The backend never reports it (see
// the file-level note), so it lives alongside the server kinds only inside this component.
type ViewerKind = ResourceRenderKind | 'word';

const canPreview = (kind?: ViewerKind) => !!kind && kind !== 'download';

/** True when the file is a `.docx` (which mammoth can render) — by extension or content type. */
const isWordDocx = (fileName?: string | null, contentType?: string | null) => {
  const name = String(fileName || '').toLowerCase();
  const type = String(contentType || '').toLowerCase();
  return (
    name.endsWith('.docx') ||
    type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
};

const KIND_LABELS: Record<string, string> = {
  pdf: 'PDF',
  markdown: 'Markdown',
  spreadsheet: 'Spreadsheet',
  presentation: 'Presentation',
  image: 'Image',
  text: 'Text',
  word: 'Word',
  video: 'Video',
};

const ResourceFileViewer: React.FC<Props> = ({
  resourceId,
  fileName,
  contentType,
  sizeBytes,
  renderKind,
  version,
  autoPreview = false,
}) => {
  const [showPreview, setShowPreview] = useState(autoPreview);
  const [download, setDownload] = useState<ResourceFileDownload | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Distinguished from `error` on purpose: a failed preview still leaves a working download,
  // so it is a degraded state rather than a failure of the step.
  const [previewUnavailable, setPreviewUnavailable] = useState('');
  const [error, setError] = useState('');

  const [markdown, setMarkdown] = useState('');
  const [textContent, setTextContent] = useState('');
  const [sheets, setSheets] = useState<SheetTable[]>([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [wordHtml, setWordHtml] = useState('');
  // A signed URL for the server-rendered PDF of a PowerPoint deck. When set, the presentation
  // renders through the PDF iframe — a faithful preview of the whole deck — instead of the
  // client-side pptx-preview. Empty means no ready server preview (fall back to pptx-preview).
  const [presentationPdfUrl, setPresentationPdfUrl] = useState('');
  // The raw .pptx bytes for the client-side pptx-preview fallback. Held in state (not drawn from
  // the load effect directly) because the target container only mounts once `loading` flips false
  // — so a dedicated effect draws into it once it is actually in the DOM. Empty means no pending
  // client-side render.
  const [presentationBuffer, setPresentationBuffer] = useState<ArrayBuffer | null>(null);
  // Set when the server reports the deck's PDF is still being built by the async worker
  // (`preview_status === 'processing'`). Shows a light "preparing" note instead of the download-
  // only fallback, since re-opening the preview shortly should find it ready.
  const [previewPending, setPreviewPending] = useState('');

  const pptxContainerRef = useRef<HTMLDivElement | null>(null);

  // A same-origin blob: URL for the pdf/image kinds. Under `Cross-Origin-Embedder-Policy:
  // require-corp` (set by the deployment, not this app) the browser refuses to frame/embed a
  // cross-origin S3 resource that lacks its own COEP/CORP header — the pdf iframe fails with
  // `CoepFrameResourceNeedsCoepHeader`. Reading the bytes and pointing the tag at a blob: URL
  // makes the resource same-origin, which COEP never blocks. (Same idea as the moodle relay.)
  const [mediaObjectUrl, setMediaObjectUrl] = useState('');

  // Every blob: URL we mint, so the reset effect and unmount can revoke them (they otherwise
  // leak for the page's lifetime).
  const objectUrlsRef = useRef<string[]>([]);
  const makeObjectUrl = useCallback((blob: Blob) => {
    const url = URL.createObjectURL(blob);
    objectUrlsRef.current.push(url);
    return url;
  }, []);
  const revokeObjectUrls = useCallback(() => {
    objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    objectUrlsRef.current = [];
  }, []);

  // Read a resource's bytes for a blob: preview — straight from the signed S3 URL when a bucket
  // GET CORS rule allows it, else through the same-origin API relay (which streams the same
  // object regardless of CORS). Used by the pdf/image kinds; the pptx preview *derivative* has
  // no relay (the relay streams the original .pptx), so it fetches directly and degrades.
  const fetchBytesWithFallback = useCallback(
    async (directUrl: string): Promise<Blob> => {
      try {
        const direct = await fetch(directUrl);
        if (!direct.ok) throw new Error(`Direct read failed (${direct.status}).`);
        return await direct.blob();
      } catch {
        const relayed = await fetchResourceFileContent(resourceId);
        if (!relayed.ok) throw new Error(`Relay read failed (${relayed.status}).`);
        return await relayed.blob();
      }
    },
    [resourceId],
  );

  // The effective render kind. Falls back to the value the download endpoint reports, so a
  // caller that has not cached `render_kind` on the item still gets the right renderer; and a
  // `.docx` that the backend calls `download` is upgraded to the client-only `word` renderer.
  const effectiveKind = useMemo<ViewerKind>(() => {
    const base = (renderKind || download?.render_kind || 'download') as ResourceRenderKind;
    if ((base === 'download' || base === '') && isWordDocx(fileName, contentType)) {
      return 'word';
    }
    return base;
  }, [renderKind, download?.render_kind, fileName, contentType]);

  const displayName = String(fileName || download?.file_name || 'file');
  const previewable = canPreview(effectiveKind);

  /** Fetch a signed URL. `inline` matters only for what the browser renders itself. */
  const fetchSignedUrl = useCallback(
    async (inline: boolean) => await getResourceFileDownload(resourceId, { inline }),
    [resourceId],
  );

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    setError('');

    try {
      // A fresh URL per click: the previous one may have expired while the modal sat open.
      const fresh = await fetchSignedUrl(false);

      const anchor = document.createElement('a');
      anchor.href = fresh.url;
      anchor.rel = 'noopener';
      if (fresh.file_name) anchor.download = fresh.file_name;

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (err: any) {
      setError(String(err?.message || 'Unable to download this file.'));
    } finally {
      setDownloading(false);
    }
  }, [fetchSignedUrl]);

  // Reset cached content whenever the file or its version changes, so a replaced file never
  // shows the previous contents.
  useEffect(() => {
    setDownload(null);
    setMarkdown('');
    setTextContent('');
    setSheets([]);
    setActiveSheet(0);
    setWordHtml('');
    setPresentationPdfUrl('');
    setPresentationBuffer(null);
    setPreviewPending('');
    setPreviewUnavailable('');
    setError('');
    revokeObjectUrls();
    setMediaObjectUrl('');
  }, [resourceId, version, revokeObjectUrls]);

  // Revoke any outstanding blob: URLs when the viewer unmounts.
  useEffect(() => () => revokeObjectUrls(), [revokeObjectUrls]);

  // Client-side pptx-preview, deferred until the container is in the DOM. The main load effect
  // cannot draw the deck itself: it holds `loading` true until its `finally`, and the container
  // only mounts once `loading` is false — so drawing from there always hit a null ref. Instead it
  // stashes the bytes in `presentationBuffer`; setting that flips loading off and mounts the
  // container, and this effect (which re-runs when the buffer lands) finds the ref populated.
  useEffect(() => {
    if (effectiveKind !== 'presentation' || !presentationBuffer || presentationPdfUrl) return;

    const container = pptxContainerRef.current;
    if (!container) return;

    let cancelled = false;

    (async () => {
      const { init } = await import('pptx-preview');
      if (cancelled) return;

      container.innerHTML = '';

      const previewer = init(container, {
        width: container.clientWidth || 900,
        height: Math.round(((container.clientWidth || 900) * 9) / 16),
        mode: 'slide',
      });

      try {
        await previewer.preview(presentationBuffer);
      } catch {
        // pptx-preview renders only a subset of PowerPoint; rich decks (embedded media, SmartArt,
        // charts) make it throw. That is a preview limitation, not a broken file — say so plainly
        // and leave the working Download button as the way in. A faithful in-app preview needs the
        // server-side render (see VIDEO_CDN_AND_PREVIEW_SPEC.md).
        if (!cancelled) {
          container.innerHTML = '';
          setPreviewUnavailable(
            'In-browser preview isn’t available for this presentation. Download it to open it in PowerPoint.',
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [effectiveKind, presentationBuffer, presentationPdfUrl]);

  // Load whatever the active renderer needs. Guarded by `cancelled` so a fast
  // close/reopen or a version bump cannot land stale content in state.
  useEffect(() => {
    if (!showPreview || !previewable) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setPreviewPending('');
      setPreviewUnavailable('');
      setError('');

      try {
        const signed = await fetchSignedUrl(
          effectiveKind === 'pdf' || effectiveKind === 'image' || effectiveKind === 'video',
        );

        if (cancelled) return;

        setDownload(signed);

        if (effectiveKind === 'video') {
          // A <video> streams straight from the signed S3 URL: a media-element load is not a CORS
          // fetch (like <img>/<iframe>), so it needs no bucket GET rule, and — unlike pdf/image —
          // it must NOT be buffered into a blob, which would pull the whole file before playback.
          // The signed URL in `download` is handed to the element's `src` in renderPreviewBody.
          return;
        }

        if (effectiveKind === 'presentation') {
          // Prefer the server-rendered PDF derivative. The conversion is asynchronous (toolbox_queue
          // worker), so the endpoint reports one of three states:
          //   ready       — the PDF exists; render it in the iframe and skip the raw .pptx entirely.
          //   processing  — enqueued but not built yet; show a light "preparing" note and stop
          //                 (re-opening the preview shortly should find it ready). We do NOT fall
          //                 through to pptx-preview here, so the note is not immediately overwritten.
          //   unavailable — no server preview will be produced; fall through to the client-side
          //                 pptx-preview attempt below (which has its own honest download fallback).
          try {
            const preview = await getResourceFilePreviewUrl(resourceId);
            if (cancelled) return;
            if (preview.preview_status === 'ready' && preview.preview_url) {
              // Same COEP reasoning as the pdf kind: the derivative is a cross-origin S3 PDF, so
              // frame it as a same-origin blob: URL. The direct GET needs a bucket CORS rule;
              // where it is absent we cannot relay the *derivative* (the API relay streams the
              // original .pptx, not the generated PDF), so fall through to the client-side
              // pptx-preview below rather than framing a URL the browser will block.
              try {
                const pdfResp = await fetch(preview.preview_url);
                if (!pdfResp.ok) throw new Error(`Preview read failed (${pdfResp.status}).`);
                const pdfBlob = await pdfResp.blob();
                if (cancelled) return;
                setPresentationPdfUrl(makeObjectUrl(pdfBlob));
                return;
              } catch {
                /* fall through to the client-side pptx-preview path */
              }
            }
            if (preview.preview_status === 'processing') {
              setPreviewPending(
                'Preview is being prepared — check back shortly. You can download the file to open it now.',
              );
              return;
            }
          } catch {
            /* best effort — fall through to the client-side pptx-preview path */
          }
        }

        if (effectiveKind === 'pdf' || effectiveKind === 'image') {
          // Not loaded directly from the signed S3 URL: under COEP require-corp the browser blocks
          // a cross-origin framed/embedded resource that lacks its own COEP/CORP header. Read the
          // bytes instead (direct from S3 where a GET CORS rule allows it, else the same-origin API
          // relay, which streams the same object) and render a same-origin blob: URL.
          try {
            const blob = await fetchBytesWithFallback(signed.url);
            if (cancelled) return;
            setMediaObjectUrl(makeObjectUrl(blob));
          } catch (err: any) {
            if (!cancelled) {
              setPreviewUnavailable(
                String(err?.message || 'This file cannot be previewed in the browser.') +
                  ' Download it to open it.',
              );
            }
          }
          return;
        }

        let response: Response;

        // Prefer fetching the bytes DIRECTLY from the signed S3 URL — one hop, no Django worker
        // held open relaying the file. That needs a GET CORS rule on the bucket for this origin;
        // where it is absent the cross-origin fetch rejects, so we fall back to the same-origin
        // API relay (streams the object back through Django), which always works. The fast path
        // is therefore opt-in by infrastructure: add the CORS rule and previews speed up with no
        // code change; until then behaviour is exactly what it was.
        try {
          const direct = await fetch(signed.url);
          if (!direct.ok) throw new Error(`Direct read failed (${direct.status}).`);
          response = direct;
        } catch {
          try {
            response = await fetchResourceFileContent(resourceId);
          } catch (err: any) {
            // Both paths failed: the object is genuinely unreadable (removed, storage
            // misconfigured, or the streaming endpoint not yet deployed) — not just a CORS block.
            if (!cancelled) {
              setPreviewUnavailable(
                String(err?.message || 'This file cannot be previewed in the browser.') +
                  ' Download it to open it.',
              );
            }
            return;
          }
        }

        if (!response.ok) {
          if (!cancelled) {
            setPreviewUnavailable(
              `Could not load the file for preview (${response.status}). Download it to open it.`,
            );
          }
          return;
        }

        if (effectiveKind === 'markdown' || effectiveKind === 'text') {
          const text = await response.text();
          if (cancelled) return;

          if (effectiveKind === 'markdown') setMarkdown(text);
          else setTextContent(text);

          return;
        }

        const buffer = await response.arrayBuffer();
        if (cancelled) return;

        if (effectiveKind === 'word') {
          // Dynamic import: mammoth is large and most steps are not Word docs.
          const mammoth = await import('mammoth');
          if (cancelled) return;

          const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
          if (cancelled) return;

          // mammoth emits HTML from the document's own markup; sanitize before injecting it,
          // the same trust posture the spreadsheet path relies on the upload allowlist for.
          setWordHtml(DOMPurify.sanitize(result.value || ''));
          return;
        }

        if (effectiveKind === 'spreadsheet') {
          // Dynamic import: SheetJS is large and most steps are not spreadsheets.
          const XLSX = await import('xlsx');
          if (cancelled) return;

          const workbook = XLSX.read(buffer, { type: 'array' });

          const tables: SheetTable[] = workbook.SheetNames.map((name) => ({
            name,
            html: DOMPurify.sanitize(
              XLSX.utils.sheet_to_html(workbook.Sheets[name], { id: `sheet-${name}` })
            ),
          }));

          setSheets(tables);
          setActiveSheet(0);
          return;
        }

        if (effectiveKind === 'presentation') {
          // The pptx-preview container is not mounted while `loading` is true (this effect keeps
          // it true until its `finally`), so it cannot be drawn into here. Stash the bytes and let
          // the dedicated effect below run the preview once the container is actually in the DOM.
          setPresentationBuffer(buffer);
          return;
        }
      } catch (err: any) {
        if (!cancelled) {
          setPreviewUnavailable(
            String(err?.message || 'This file cannot be previewed. Download it to open it.'),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    showPreview,
    previewable,
    effectiveKind,
    fetchSignedUrl,
    resourceId,
    version,
    makeObjectUrl,
    fetchBytesWithFallback,
  ]);

  const metaLine = [
    KIND_LABELS[String(effectiveKind)] || contentType || '',
    formatFileSize(sizeBytes ?? download?.size_bytes),
  ]
    .filter(Boolean)
    .join(' · ');

  const renderPreviewBody = () => {
    if (loading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
          <HbrSpinner />
        </div>
      );
    }

    if (previewPending) {
      // The async worker is still building the PDF. A light, non-alarming note — the file is fully
      // available via the Download button while the preview catches up.
      return (
        <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
          <HbrSpinner />
          <span>{previewPending}</span>
        </div>
      );
    }

    if (previewUnavailable) {
      return (
        <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
          <HbrIcon asset={alertsWarningSmall} />
          <span>{previewUnavailable}</span>
        </div>
      );
    }

    if (effectiveKind === 'pdf' && mediaObjectUrl) {
      return (
        <iframe
          src={mediaObjectUrl}
          title={displayName}
          style={{ width: '100%', height: '640px', border: '1px solid #E1E4E8', borderRadius: '4px' }}
        />
      );
    }

    if (effectiveKind === 'image' && mediaObjectUrl) {
      return (
        <img
          src={mediaObjectUrl}
          alt={displayName}
          style={{ maxWidth: '100%', borderRadius: '4px', border: '1px solid #E1E4E8' }}
        />
      );
    }

    if (effectiveKind === 'video' && download?.url) {
      return (
        <video
          src={download.url}
          controls
          // Download is deliberately not offered for video: `nodownload` drops Chromium's button,
          // the right-click menu (the other "Save video as" route) is blocked, and Picture-in-
          // Picture (its own save affordance) is disabled. Matches JourneyMapVideoPlayer.
          controlsList="nodownload noremoteplayback"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
          style={{
            width: '100%',
            maxHeight: '640px',
            background: '#000',
            borderRadius: '4px',
            border: '1px solid #E1E4E8',
          }}
        />
      );
    }

    if (effectiveKind === 'markdown' && markdown) {
      return (
        <div className="journey-map-markdown" style={{ fontSize: '14px', lineHeight: 1.6 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
        </div>
      );
    }

    if (effectiveKind === 'text' && textContent) {
      return (
        <pre
          style={{
            margin: 0,
            padding: '12px',
            maxHeight: '520px',
            overflow: 'auto',
            background: '#F7F7F7',
            border: '1px solid #E1E4E8',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {textContent}
        </pre>
      );
    }

    if (effectiveKind === 'word' && wordHtml) {
      return (
        <div
          className="journey-map-markdown"
          style={{
            fontSize: '14px',
            lineHeight: 1.6,
            maxHeight: '640px',
            overflow: 'auto',
            padding: '16px',
            background: '#FFFFFF',
            border: '1px solid #E1E4E8',
            borderRadius: '4px',
          }}
          // Sanitized by DOMPurify above before it reached state.
          dangerouslySetInnerHTML={{ __html: wordHtml }}
        />
      );
    }

    if (effectiveKind === 'spreadsheet' && sheets.length > 0) {
      return (
        <div>
          {sheets.length > 1 && (
            <div
              style={{
                display: 'flex',
                gap: '6px',
                flexWrap: 'wrap',
                marginBottom: '10px',
              }}
            >
              {sheets.map((sheet, index) => (
                <button
                  key={sheet.name}
                  type="button"
                  onClick={() => setActiveSheet(index)}
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    border: `1px solid ${index === activeSheet ? '#1D69CC' : '#E1E4E8'}`,
                    background: index === activeSheet ? '#EAF3FF' : '#FFFFFF',
                    color: index === activeSheet ? '#1D69CC' : '#39414D',
                  }}
                >
                  {sheet.name}
                </button>
              ))}
            </div>
          )}

          <div
            className="resource-file-sheet"
            style={{
              maxHeight: '520px',
              overflow: 'auto',
              border: '1px solid #E1E4E8',
              borderRadius: '4px',
              fontSize: '12px',
            }}
            // SheetJS emits a plain <table> from the workbook's cell values. It is not a
            // sanitizer, so this is only as safe as the upload allowlist that produced the
            // file — which is the same trust boundary as opening it in Excel.
            dangerouslySetInnerHTML={{ __html: sheets[activeSheet]?.html || '' }}
          />
        </div>
      );
    }

    if (effectiveKind === 'presentation') {
      // Server-rendered PDF available: render it a slide at a time (a deck should read as slides,
      // not one long scrolling document — which is all the native PDF iframe offers).
      if (presentationPdfUrl) {
        return (
          <Suspense
            fallback={
              <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
                <HbrSpinner />
              </div>
            }
          >
            <PdfSlideViewer src={presentationPdfUrl} title={displayName} heightPx={640} />
          </Suspense>
        );
      }

      // No server preview — the client-side pptx-preview writes into this container (and, on a
      // rich deck it cannot render, sets previewUnavailable so the honest fallback shows instead).
      return (
        <div
          ref={pptxContainerRef}
          style={{
            width: '100%',
            minHeight: '420px',
            border: '1px solid #E1E4E8',
            borderRadius: '4px',
            overflow: 'auto',
            background: '#FFFFFF',
          }}
        />
      );
    }

    return null;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#23282E' }}>{displayName}</div>
          {metaLine && (
            <div style={{ fontSize: '12px', color: '#6F7680', marginTop: '2px' }}>{metaLine}</div>
          )}
        </div>

        {previewable && (
          <StbButton
            role="default"
            icon={arrowSquareOut}
            onClick={() => setShowPreview((open) => !open)}
          >
            {showPreview ? 'Hide Preview' : 'Preview'}
          </StbButton>
        )}

        {/* No download affordance for video: it is a tracked, stream-only step (matches the
            learner-facing JourneyMapVideoPlayer). Every other kind keeps a download that always
            works even when its in-app preview cannot render. */}
        {effectiveKind !== 'video' && (
          <StbButton
            role="default"
            icon={downloadSimple}
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? 'Preparing...' : 'Download File'}
          </StbButton>
        )}
      </div>

      {error && <div style={{ fontSize: '12px', color: '#CC2D37' }}>{error}</div>}

      {showPreview && previewable && <div>{renderPreviewBody()}</div>}
    </div>
  );
};

export default ResourceFileViewer;
